// WhatsApp Inbox Types

import { ApplicationStatusEnum } from "src/utils/enums/ApplicationStatus";
import { ApplicantListItem } from "../applicants/ApplicantListItem";
import { RequiredPlaceholdersEnum } from "./WhatsappTemplatesTypes";



export enum ConversationTask {
  SCREENING = 'SCREENING', // AI interview from invitation to completion
  SCHEDULING = 'SCHEDULING', // WhatsApp template with interview scheduling link
  INBOX = 'INBOX', // Chat with recruiter (DEFAULT)
}


export enum ConversationStage {
  INITIAL_CHAT = 'INITIAL_CHAT',
  JOB_SEARCH = 'JOB_SEARCH',
  APPLICATION_SUBMITTED = 'APPLICATION_SUBMITTED',
  INTERVIEW_SCHEDULED = 'INTERVIEW_SCHEDULED',
  OFFER_MADE = 'OFFER_MADE',
  HIRED = 'HIRED',
  REJECTED = 'REJECTED',
  TERMINATED_COUNTRY_NOT_ALLOWED = 'TERMINATED_COUNTRY_NOT_ALLOWED',
}

export enum MessageDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

export enum MessageType {
  TEXT = 'text',
  IMAGE = 'image',
  DOCUMENT = 'document',
  VIDEO = 'video',
  AUDIO = 'audio',
  LOCATION = 'location',
  TEMPLATE = 'template',
  INTERACTIVE = 'interactive',
}

export enum MessageStatus {
  SENT = 'sent',
  DELIVERED = 'delivered',
  READ = 'read',
  FAILED = 'failed',
}

export interface WorkerSummaryDto {
  id: number;
  externalId: string;
  firstName: string;
  lastName: string;
  nationality?: string;
  city?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  nationalityCategory?: string;
  gender?: string;
  isOfflineApplication?: boolean;
  isUnknown?: boolean;
}

export interface LastMessageDto {
  content: string;
  timestamp: Date;
  direction: MessageDirection;
  messageType: MessageType;
  status?: MessageStatus; // Status for outbound messages
}

export enum ConversationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  EXPIRED = 'EXPIRED',
}

export enum TemplateSourceEnum {
  JOB_APPLICATION = 'JOB_APPLICATION',
  CV_SEARCH = 'CV_SEARCH',
  RECRUITER_INBOX = 'RECRUITER_INBOX',
}

export const isJobOnlyTemplates = (requiredPlaceholders: RequiredPlaceholdersEnum[]) => {
  if (!requiredPlaceholders || requiredPlaceholders?.length === 0) return false;
  for (const placeholder of requiredPlaceholders) {
    if (placeholder === RequiredPlaceholdersEnum.JOB_TITLE || placeholder === RequiredPlaceholdersEnum.JOB_LOCATION) {
      return true;
    }
  }
  return false;
}

export interface ConversationListItemDto {
  id: string;
  task: ConversationTask;
  status: ConversationStatus;
  unreadCount: number;
  lastMessage: LastMessageDto | null;
  lastMessageAt: number;
  lastInboundMessageAt: Date;
  worker: WorkerSummaryDto | null;
  assignedToUserId?: number;
  aiHandled: boolean;
  whatsappPhone: string;
  createdAt: number;
  canSendText: boolean;
  remainingHoursToSendTxt: number;
  rtmToken?: string;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  content?: string;
  messageType: MessageType;
  direction: MessageDirection;
  status: MessageStatus;
  mediaUrl?: string;
  sentAt: number;
  deliveredAt?: Date;
  readAt?: Date;
  createdAt: Date;
  sentByUserId?: number; // null/undefined = AI sent, number = recruiter userId
  sentByUserExternalId?: string; // null/undefined = recruiter sent, string = jobseeker external ID
  sentByUserName?: string;
}

export interface ListConversationsFilters {
  page?: number;
  limit?: number;
  assignedToUserId?: number;
  stage?: ConversationStage;
  unreadOnly?: boolean;
  search?: string;
}

export interface ConversationsResponse {
  conversations: ConversationListItemDto[];
  total: number;
  page: number;
  rtmToken: string;
  channelId: string;
}

export interface MessagesResponse {
  messages: MessageDto[];
  total: number;
}

export interface SendMessageRequest {
  content: string;
  templateId?: number;
}

export interface SendMessageResponse {
  content: string;
  conversationId: string;
  createdAt: Date;
  direction: MessageDirection;
  id: string;
  messageType: MessageType;
  sentAt: number;
  sentByUserId: number;
  sentByUserExternalId?: string;
  status: MessageStatus;
}

export interface WhatsAppInboxState {
  // Conversations
  conversations: ConversationListItemDto[];
  conversationsTotal: number;
  conversationsPage: number;
  rtmToken: string | null;
  channelId: string | null;
  isLoadingConversations: boolean;
  isLoadingMoreConversations: boolean;

  // Active conversation
  activeConversationId: string | null; // Changed to UUID string

  // Messages (keyed by conversationId - UUID string)
  messages: Record<string, MessageDto[]>;
  messagesTotal: Record<string, number>;
  messagesPage: Record<string, number>;
  isLoadingMessages: boolean;

  // Notes (keyed by conversationId - UUID string)
  notes: Record<string, ConversationNoteDto[]>;
  isLoadingNotes: boolean;
  isSavingNote: boolean;

  // Conversation metadata (keyed by conversationId - UUID string)
  canSendText: Record<string, boolean>;

  // Filters
  filters: ListConversationsFilters;

  // Active tab
  activeWhatsappInboxTab: WhatsappInboxTabEnum;

  activeWhatsappProfileTab: WhatsappProfileTabEnum;

  // UI state
  isSendingMessage: boolean;
  error: string | null;

  // Application history
  applicationHistory: GetApplicationHistoryResponseData;
  isLoadingApplicationHistory: boolean;
  applicationHistoryTotal: number;
  applicationHistoryPage: number;

  // Recent application details
  recentApplicationDetails: ApplicantListItem | null;
  isLoadingRecentApplicationDetails: boolean;
}

export enum WhatsappInboxTabEnum {
  RECRUITER = 'recruiter',
  SAUD = 'saud',
}

export enum WhatsappProfileTabEnum {
  PROFILE = 'profile',
  APPLICATION_HISTORY = 'application_history',
  NOTES = 'notes',
}

export interface MessageComposerProps {
  conversation: ConversationListItemDto;
  onSendMessage: (content: string, templateId?: string) => Promise<void>;
  isSending: boolean;
}

export interface TemplateSelectorProps {
  isOpen: boolean;
  onToggle: () => void;
  templates: any[];
  loadingTemplates: boolean;
  selectedTemplate: any | null;
  onSelect: (template: any) => void;
  isExpired: boolean;
}

export interface TemplatePreviewProps {
  template: any;
  onSend: () => void;
  isSending: boolean;
  isExpired: boolean;
  renderTemplateContent: (content: string) => JSX.Element;
}

export interface MessageInputProps {
  messageText: string;
  onMessageChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSend: () => void;
  isSending: boolean;
  isExpired: boolean;
  textareaRef: React.RefObject<HTMLInputElement>;
  placeholder: string;
  taskInProgress: boolean;
  conversationId: string;
}

export interface GetApplicationHistoryResponseData {
  recentApplication: ApplicationItemDto | null;
  applicationHistory: ApplicationItemDto[];
  total: number;
}

export interface ApplicationItemDto {
  externalId: string;
  isOffline: boolean;
  appliedAt: number | null;
  jobTitle: string;
  status: ApplicationStatusEnum;
  stageName?: string;
}

// Notes
export interface ConversationNoteDto {
  id: number;
  conversationId: string;
  content: string;
  createdAt: number; // unix seconds
  updatedAt?: number; // unix seconds
  createdByUserId?: string | number;
  createdByName?: string;
}

export interface ListConversationNotesResponse {
  notes: ConversationNoteDto[];
}

export interface WhatsappMessageDto {
  id: number;
  content: string; // text in message
  direction: 'inbound' | 'outbound';
  mediaUrl: string | null;
  messageType: 'text' | string;
  sentAt: number; //
  status: string; // "sent"
}

export interface AgoraRTMEvent {
  type: 'message:new' | 'message:status' | 'conversation:assigned' | 'task:status';
  data: {
    conversationId: string;
    message: WhatsappMessageDto;
    status: string; // "sent"
    deliveredAt?: number; //
    readAt?: number; //
    messageId?: string;
    assignedToUserId?: number;
    aiHandled?: boolean;
    currentStatus?: ConversationStatus;
    currentTask?: ConversationTask;
  };
  timestamp: number;
}

export enum WhatsappSendMessageState {
  CAN_SEND_TEMPLATE_ONLY = 'CAN_SEND_TEMPLATE_ONLY',
  CAN_SEND_TEMPLATE_AND_TEXT = 'CAN_SEND_TEMPLATE_AND_TEXT',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON_WITH_HOURS_LEFT = 'EXPIRING_SOON_WITH_HOURS_LEFT',
  HAS_PENDING_TASK = 'HAS_PENDING_TASK',
}
